import {
  useState } from "react";
import { Box,
  IconButton,
  TextField,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { Search } from "@mui/icons-material";
import { ClearIcon } from "@mui/x-date-pickers";
import MUIDataTable from "mui-datatables";
import { MuiTheme } from "../styles/MuiDataTableStyle";
import { textLabels } from "../utils/muiTableOptions";
import { useStateAuthValue } from "../context/AuthState";
import { useToast } from "../context/Toast/ToastProvider";
import { showToast, ToastType } from "../utils/ToastUtil";
import { LoadingModal } from "../components/Modals/LoadingModal";
import { validateCard, validateCbu } from "../services/cardValidatorService";
import PageHeader from "../components/PageHeader";

// Traducción de los campos (en inglés en la API) al español sólo para
// renderizar. Las claves/valores subyacentes se mantienen en inglés.
const CARD_FIELD_LABELS = {
  cardNumber: "Número de tarjeta",
  valid: "Válida",
  luhnValid: "Luhn válido",
  formatValid: "Formato válido",
  type: "Marca",
};

const CBU_FIELD_LABELS = {
  cbu: "CBU / CVU",
  valid: "Válido",
  entity: "Entidad",
  entityCode: "Código de entidad",
};

const formatValue = (value) => {
  if (typeof value === "boolean") return value ? "Sí" : "No";
  if (value === null || value === undefined || value === "") return "—";
  return value;
};

const toRows = (result, labels) =>
  Object.entries(labels).map(([key, label]) => ({
    campo: label,
    valor: formatValue(result[key]),
  }));

const tableOptions = {
  selectableRows: "none",
  textLabels,
  search: false,
  download: false,
  print: false,
  filter: false,
  viewColumns: false,
  sort: false,
  pagination: false,
  responsive: "standard",
  elevation: 0,
};

const tableColumns = [
  { name: "campo", label: "Campo" },
  { name: "valor", label: "Valor" },
];

const tableWrapperSx = {
  width: "100%",
  "& .MuiTableHead-root .MuiTableRow-root .MuiTableCell-head": {
    span: { justifyContent: "center" },
  },
};

const CardValidators = () => {
  const [{ userToken }] = useStateAuthValue();
  const toastDispatch = useToast();
  const displayError = (msg) =>
    showToast(toastDispatch, msg, ToastType.ERROR, ToastType.ERROR);

  const [loading, setLoading] = useState(false);

  const [cardNumber, setCardNumber] = useState("");
  const [cardResult, setCardResult] = useState(null);

  const [cbu, setCbu] = useState("");
  const [cbuResult, setCbuResult] = useState(null);

  const handleValidateCard = async () => {
    if (!cardNumber.trim()) return;
    setLoading(true);
    const res = await validateCard(userToken, cardNumber.trim());
    setLoading(false);
    if (res && typeof res.valid === "boolean") {
      setCardResult(res);
    } else {
      setCardResult(null);
      displayError("No se pudo validar la tarjeta, intente más tarde");
    }
  };

  const handleValidateCbu = async () => {
    if (!cbu.trim()) return;
    setLoading(true);
    const res = await validateCbu(userToken, cbu.trim());
    setLoading(false);
    if (res && typeof res.valid === "boolean") {
      setCbuResult(res);
    } else {
      setCbuResult(null);
      displayError("No se pudo validar el CBU, intente más tarde");
    }
  };

  return (
    <ThemeProvider theme={MuiTheme}>
      <div className="users">
        <div className="users-table-container">
          {/* Validar tarjeta */}
          <PageHeader title="Validar tarjeta" />

          <Box
            sx={{ display: "flex", alignItems: "center", marginBottom: "10px" }}
          >
            <IconButton onClick={handleValidateCard}>
              <Search />
            </IconButton>
            <TextField
              variant="standard"
              value={cardNumber}
              onChange={(event) => setCardNumber(event.target.value)}
              onKeyDown={(event) =>
                event.key === "Enter" && handleValidateCard()
              }
              placeholder="Número de tarjeta..."
              InputProps={{ "aria-label": "Número de tarjeta" }}
            />
            <IconButton
              onClick={() => {
                setCardNumber("");
                setCardResult(null);
              }}
            >
              <ClearIcon />
            </IconButton>
          </Box>

          {cardResult && (
            <Box sx={tableWrapperSx}>
              <MUIDataTable
                title=""
                data={toRows(cardResult, CARD_FIELD_LABELS)}
                columns={tableColumns}
                options={tableOptions}
              />
            </Box>
          )}

          {/* Validar CBU / CVU */}
          <PageHeader title="Validar CBU / CVU" sx={{ marginTop: "30px" }} />

          <Box
            sx={{ display: "flex", alignItems: "center", marginBottom: "10px" }}
          >
            <IconButton onClick={handleValidateCbu}>
              <Search />
            </IconButton>
            <TextField
              variant="standard"
              value={cbu}
              onChange={(event) => setCbu(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && handleValidateCbu()}
              placeholder="CBU / CVU (22 dígitos)..."
              InputProps={{ "aria-label": "CBU o CVU" }}
            />
            <IconButton
              onClick={() => {
                setCbu("");
                setCbuResult(null);
              }}
            >
              <ClearIcon />
            </IconButton>
          </Box>

          {cbuResult && (
            <Box sx={tableWrapperSx}>
              <MUIDataTable
                title=""
                data={toRows(cbuResult, CBU_FIELD_LABELS)}
                columns={tableColumns}
                options={tableOptions}
              />
            </Box>
          )}
        </div>
      </div>

      {loading && <LoadingModal />}
    </ThemeProvider>
  );
};

export default CardValidators;
